import { useQuery } from '@apollo/client'
import { Stack, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { profileHasEarnedItems, profileHideOwnedItems } from '@/constants/blockchain'
import {
  GetUserItemsCount,
  QUERY_GET_CREATED_ITEMS,
  QUERY_GET_EARNED_ITEMS,
  QUERY_GET_OWNED_ITEMS,
  QUERY_GET_SUGGESTED_CREATED_ITEMS,
  QUERY_GET_SUGGESTED_EARNED_ITEMS,
  QUERY_GET_SUGGESTED_OWNED_ITEMS,
  QUERY_GET_USER_ITEMS_COUNT,
} from '@/schemas/tokens.schema'
import { formatValueToSuffix } from '@/utils/utils'

import { Tabs } from '../Explore/ExplorePage'
import ExploreTab from '../Explore/ExploreTab'
import GetStarted from '../GetStarted'
import PageSelector from '../PageSelector'

interface ProfileItemsProps {
  profileId: string
  isMyProfile: boolean
}

const ProfileItems: React.FC<ProfileItemsProps> = ({ profileId, isMyProfile }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

  const { data, loading, error } = useQuery<GetUserItemsCount>(QUERY_GET_USER_ITEMS_COUNT, {
    variables: {
      queryId: profileId.toLowerCase(),
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  const numberOfOwnedItems = useMemo(() => data?.totalOwned?.agg?.count || 0, [data])
  const numberOfPublishedItems = useMemo(() => data?.totalPublished?.agg?.count || 0, [data])
  const numberOfEarnedItems = useMemo(() => data?.totalEarned?.agg?.count || 0, [data])

  // Define tabs
  const publishedTab: Tabs = useMemo(
    () => ({
      id: 'published',
      contentType: 'user_published',
      total: numberOfPublishedItems,
      title: t('components.profile.published', 'Published'),
      filterOptions: ['All', 'Unique', 'Fungible'],
      sortOptions: ['# transfers', 'publish date'],
      query: QUERY_GET_CREATED_ITEMS,
      suggestionQuery: QUERY_GET_SUGGESTED_CREATED_ITEMS,
      queryLimit: 6,
      cols: {
        base: 2,
        sm: 2,
        md: 3,
        lg: 6,
      },
    }),
    [t, numberOfPublishedItems]
  )

  const ownedTab: Tabs = useMemo(
    () => ({
      id: 'owned',
      contentType: 'user_owned',
      total: numberOfOwnedItems,
      title: t('components.profile.owned', 'Owned'),
      filterOptions: ['All', 'Unique', 'Fungible'],
      sortOptions: ['publish date'],
      query: QUERY_GET_OWNED_ITEMS,
      suggestionQuery: QUERY_GET_SUGGESTED_OWNED_ITEMS,
      queryLimit: 6,
      cols: {
        base: 2,
        sm: 2,
        md: 3,
        lg: 6,
      },
    }),
    [t, numberOfOwnedItems]
  )

  const earnedTab: Tabs = useMemo(
    () => ({
      id: 'earned',
      contentType: 'user_earned',
      total: numberOfEarnedItems,
      title: t('components.profile.earned', 'Earned'),
      filterOptions: ['All', 'Unique', 'Fungible'],
      sortOptions: ['publish date'],
      query: QUERY_GET_EARNED_ITEMS,
      suggestionQuery: QUERY_GET_SUGGESTED_EARNED_ITEMS,
      queryLimit: 6,
      cols: {
        base: 2,
        sm: 2,
        md: 3,
        lg: 6,
      },
    }),
    [t, numberOfEarnedItems]
  )

  // Setup tabs
  const tabs: Tabs[] = useMemo(() => {
    // Published tab is always shown
    const shownTabs = [publishedTab]
    // Owned tab is shown in my own profile or if hide owned items is not active
    if (!profileHideOwnedItems || isMyProfile) {
      shownTabs.push(ownedTab)
    }
    // Earned tab is shown if enabled
    if (profileHasEarnedItems) {
      shownTabs.push(earnedTab)
    }
    return shownTabs
  }, [publishedTab, ownedTab, earnedTab, isMyProfile, profileHideOwnedItems, profileHasEarnedItems])

  // Current tab
  const [currTabId, setCurrTabId] = useState<string>(tabs[0].id)

  const numberOfTotalItems = useMemo(() => {
    if (currTabId === 'published') {
      return numberOfPublishedItems
    }
    if (currTabId === 'owned') {
      return numberOfOwnedItems
    }
    return numberOfEarnedItems
  }, [currTabId, numberOfPublishedItems, numberOfOwnedItems, numberOfEarnedItems])

  return (
    <Stack style={{ margin: isMobile ? '0 15px' : '30px 0px' }}>
      <Stack
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: '40px',
        }}
      >
        {tabs.map(tab => (
          <PageSelector
            key={tab.id}
            active={currTabId === tab.id}
            onClick={() => setCurrTabId(tab.id)}
            text={tab.title}
            count={formatValueToSuffix(tab.total, 4, 0)}
          />
        ))}
      </Stack>
      <Stack style={{ minHeight: '100vh' }}>
        {tabs.map(
          tab =>
            currTabId === tab.id && (
              <ExploreTab
                key={tab.id}
                id={tab.id}
                title={tab.title}
                // Specific
                contentType={tab.contentType}
                query={tab.query}
                suggestionQuery={tab.suggestionQuery}
                total={tab.total}
                cols={tab.cols}
                queryId={profileId.toLowerCase()}
                // Optional
                tooltip={tab.tooltip}
                filterOptions={tab.filterOptions}
                sortOptions={tab.sortOptions}
                triggerRefetch={() => {}}
                isAdmin={false}
              />
            )
        )}
        {!loading && !error && numberOfTotalItems === 0 && isMyProfile && <GetStarted />}
      </Stack>
    </Stack>
  )
}

export default ProfileItems
